import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router";
import { setupRequestSchema } from "@wodiary/shared";
import { api } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.js";
import { errorMessage } from "../lib/errors.js";
import { HeartIcon, LockIcon } from "../components/icons.js";

/**
 * 一次性初始化（spec §5.1.1）。
 *
 * 在这**一次**里创建两个账号 —— 此后服务端永久拒绝再次初始化，应用内也没有
 * 任何其它注册入口。这是「封闭性」在界面上唯一的体现。
 *
 * 创建成功后自动以第一个人登录，并送去配对页：初始化只建账号，**不建配对**
 * （spec §5.1.1 AC），接下来该做的事就是配对。
 */
export function SetupPage() {
  const { initialized, loading, user, login } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState({ username: "", password: "" });
  const [partner, setPartner] = useState({ username: "", password: "" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!loading && initialized === true && user === null) {
    return <Navigate to="/login" replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const people = [me, partner].map((p) => ({
      username: p.username.trim(),
      password: p.password,
    }));
    const parsed = setupRequestSchema.safeParse({ people });
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setError(issue?.message ?? "检查一下填的内容。");
      return;
    }
    if (people[0]?.username === people[1]?.username) {
      setError("两个人的昵称不能一样。");
      return;
    }

    setBusy(true);
    try {
      await api.setup(parsed.data.people);
      // 初始化只建账号，不建会话 —— 以第一个账号登录，进入配对。
      const first = parsed.data.people[0];
      if (first !== undefined) {
        await login(first.username, first.password);
      }
      navigate("/pairing", { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth">
      <form className="auth-card" onSubmit={onSubmit}>
        <div className="auth-brand">
          <span className="brand-mark">
            <HeartIcon size={18} style={{ color: "#fff" }} />
          </span>
          <span>我们日记</span>
        </div>
        <div>
          <h1 className="auth-title">建立你们的日记本</h1>
          <p className="auth-sub" style={{ marginTop: 6 }}>
            这一步会创建你们两个人的账号，各自有独立的昵称和密码。此后不再接受
            任何新的账号创建。
          </p>
        </div>

        {error !== null && <div className="banner banner-error">{error}</div>}

        <PersonFields
          legend="我"
          value={me}
          onChange={setMe}
          placeholder="你的昵称"
        />
        <hr className="hr-yellow" />
        <PersonFields
          legend="TA"
          value={partner}
          onChange={setPartner}
          placeholder="TA 的昵称"
        />

        <button
          type="submit"
          className="btn btn-primary btn-block"
          disabled={
            busy ||
            me.username.trim() === "" ||
            me.password === "" ||
            partner.username.trim() === "" ||
            partner.password === ""
          }
        >
          {busy ? "创建中…" : "创建两个账号"}
        </button>

        <div className="banner banner-info">
          <LockIcon size={16} />
          <span>
            暂时不做端到端加密：日记以可读形式存在你们自己的服务器上，隔离由应用层
            保证 —— 只写给自己看的日记，对方连它存在都不知道。
          </span>
        </div>
      </form>
    </div>
  );
}

function PersonFields({
  legend,
  value,
  onChange,
  placeholder,
}: {
  legend: string;
  value: { username: string; password: string };
  onChange: (next: { username: string; password: string }) => void;
  placeholder: string;
}) {
  return (
    <fieldset className="stack-sm" style={{ border: "none" }}>
      <legend className="field-label">{legend}</legend>
      <span className="field">
        <input
          value={value.username}
          onChange={(e) => onChange({ ...value, username: e.target.value })}
          placeholder={placeholder}
          autoComplete="off"
        />
      </span>
      <span className="field">
        <input
          type="password"
          value={value.password}
          onChange={(e) => onChange({ ...value, password: e.target.value })}
          placeholder="密码（至少 8 位）"
          autoComplete="new-password"
        />
      </span>
    </fieldset>
  );
}
